import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Loader2,
  User,
  Building2,
  FileText,
  Receipt,
  CreditCard,
  Banknote,
  Package,
  ChevronRight,
  X,
} from 'lucide-react';
import { globalSearch } from '../utils/searchApi';
import {
  defaultSearchFallbackRoute,
  mapSearchHitRoute,
  seeAllGroupRoute,
} from '../utils/searchNavigation';
import type { SearchEntity, SearchGroup, SearchHit, SearchResponse } from '../types/search';

// ── Entity icon map ────────────────────────────────────────────────────────────

const ENTITY_ICON: Record<SearchEntity, React.ComponentType<{ className?: string }>> = {
  customer: User,
  vendor: Building2,
  invoice: FileText,
  bill: Receipt,
  customer_payment: CreditCard,
  bill_payment: Banknote,
  item: Package,
};

// ── Flatten groups for keyboard nav ───────────────────────────────────────────

interface FlatHit { hit: SearchHit; group: SearchGroup }

function flattenHits(groups: SearchGroup[]): FlatHit[] {
  return groups.flatMap((group) => group.items.map((hit) => ({ hit, group })));
}

function hitGlobalIndex(results: SearchResponse, groupIdx: number, hitIdx: number): number {
  let count = 0;
  for (let g = 0; g < groupIdx; g++) count += results.groups[g].items.length;
  return count + hitIdx;
}

// ── Debounce hook ──────────────────────────────────────────────────────────────

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

// ── Component ─────────────────────────────────────────────────────────────────

interface GlobalSearchProps {
  /** When true renders a compact icon-only trigger (for collapsed sidebar) */
  collapsed?: boolean;
}

export default function GlobalSearch({ collapsed = false }: GlobalSearchProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  // Overlay two-step state: `overlayOpen` mounts the DOM, `visible` drives CSS transition
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [visible, setVisible] = useState(false);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [error, setError] = useState<string | null>(null);

  const debouncedQuery = useDebounce(query.trim(), 250);

  // ── Open / close ───────────────────────────────────────────────────────────

  function openOverlay() {
    setOverlayOpen(true);
    // Two rAF frames so the element is in the DOM before toggling transition classes
    requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
  }

  const closeOverlay = useCallback(() => {
    setVisible(false);
    setTimeout(() => {
      setOverlayOpen(false);
      setQuery('');
      setResults(null);
      setError(null);
      setActiveIndex(-1);
    }, 150);
  }, []);

  // Auto-focus input when overlay mounts
  useEffect(() => {
    if (overlayOpen) {
      const id = setTimeout(() => inputRef.current?.focus(), 20);
      return () => clearTimeout(id);
    }
  }, [overlayOpen]);

  // Close on Escape (global listener while overlay is open)
  useEffect(() => {
    if (!overlayOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') closeOverlay();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [overlayOpen, closeOverlay]);

  // Open with "/" key when overlay is closed and no input is focused
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (overlayOpen) return;
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === '/') {
        e.preventDefault();
        openOverlay();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [overlayOpen]);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (debouncedQuery.length < 1) {
      setResults(null);
      setError(null);
      setActiveIndex(-1);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    globalSearch({ q: debouncedQuery, limit: 5 })
      .then((data) => {
        if (!cancelled) { setResults(data); setActiveIndex(-1); }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Search failed');
          setResults(null);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [debouncedQuery]);

  // ── Navigate to hit ────────────────────────────────────────────────────────

  const goToHit = useCallback((hit: SearchHit) => {
    navigate(mapSearchHitRoute(hit));
    closeOverlay();
  }, [navigate, closeOverlay]);

  // ── Keyboard nav inside input ──────────────────────────────────────────────

  const allHits = results ? flattenHits(results.groups) : [];

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (allHits.length === 0 ? -1 : (i + 1) % allHits.length));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (allHits.length === 0 ? -1 : (i <= 0 ? allHits.length - 1 : i - 1)));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && allHits[activeIndex]) {
        goToHit(allHits[activeIndex].hit);
      } else if (query.trim().length >= 1) {
        navigate(defaultSearchFallbackRoute(query.trim()));
        closeOverlay();
      }
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  const hasResults = results && results.groups.some((g) => g.items.length > 0);
  const noResults  = results && !hasResults && !loading;
  const showEmpty  = debouncedQuery.length < 1 && !loading;

  return (
    <>
      {/* ── Trigger ────────────────────────────────────────────────────────── */}
      {collapsed ? (
        /* Collapsed sidebar: icon-only button, same style as NavButton */
        <button
          type="button"
          onClick={openOverlay}
          aria-label="Open search"
          title="Search  (/)"
          className="group w-full flex items-center justify-center px-0 py-2.5 rounded-lg
            text-slate-500 hover:bg-slate-800/60 hover:text-slate-300 transition-all duration-200"
        >
          <Search className="h-[18px] w-[18px]" />
        </button>
      ) : (
        /* Expanded sidebar: full pill that matches sidebar nav style */
        <button
          type="button"
          onClick={openOverlay}
          aria-label="Open search"
          className="group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
            text-slate-400 hover:bg-slate-800/60 hover:text-slate-200 transition-all duration-200"
        >
          <Search className="flex-shrink-0 h-[18px] w-[18px] text-slate-500 group-hover:text-slate-300" />
          <span className="flex-1 text-left truncate">Search…</span>
          <kbd className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono
            bg-slate-800 text-slate-500 rounded border border-slate-700 leading-none">
            /
          </kbd>
        </button>
      )}

      {/* ── Spotlight overlay ─────────────────────────────────────────────── */}
      {overlayOpen && createPortal(
        <div
          className={`fixed inset-0 z-[9999] flex flex-col items-center pt-[14vh] px-4
            transition-all duration-150 ease-out
            ${visible ? 'bg-black/30 backdrop-blur-sm' : 'bg-transparent backdrop-blur-none'}`}
          onMouseDown={(e) => { if (e.target === e.currentTarget) closeOverlay(); }}
        >
          {/* Panel */}
          <div
            className={`w-full max-w-[600px] bg-white rounded-2xl shadow-2xl overflow-hidden
              transition-all duration-150 ease-out
              ${visible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 -translate-y-2'}`}
          >
            {/* ── Input row ─────────────────────────────────────────────── */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
              {loading
                ? <Loader2 className="h-5 w-5 text-indigo-400 animate-spin flex-shrink-0" />
                : <Search className="h-5 w-5 text-gray-400 flex-shrink-0" />
              }
              <input
                ref={inputRef}
                type="text"
                role="combobox"
                aria-expanded={hasResults ? true : false}
                aria-autocomplete="list"
                placeholder="Search customers, invoices, items…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1 text-base text-gray-900 placeholder-gray-400 bg-transparent outline-none"
              />
              <button
                type="button"
                onClick={closeOverlay}
                aria-label="Close search"
                className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md border border-gray-200 transition-colors"
              >
                <X className="h-3 w-3" />
                <span>Esc</span>
              </button>
            </div>

            {/* ── Results panel ─────────────────────────────────────────── */}
            <div className="max-h-[420px] overflow-y-auto overscroll-contain">

              {/* Empty / prompt state */}
              {showEmpty && (
                <div className="py-10 text-center">
                  <Search className="h-8 w-8 text-gray-200 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">
                    Search across customers, invoices, bills, payments and items
                  </p>
                  <p className="text-xs text-gray-300 mt-1">Type to get started</p>
                </div>
              )}

              {/* Error */}
              {error && (
                <p className="text-sm text-red-500 text-center py-8 px-6">{error}</p>
              )}

              {/* No results */}
              {noResults && !error && (
                <div className="py-10 text-center">
                  <p className="text-sm text-gray-500">
                    No results for{' '}
                    <span className="font-semibold text-gray-700">&ldquo;{results!.query}&rdquo;</span>
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Try a different spelling or search term</p>
                </div>
              )}

              {/* Groups */}
              {hasResults &&
                results!.groups.map((group, groupIdx) => {
                  if (group.items.length === 0) return null;
                  return (
                    <div
                      key={group.type}
                      className={groupIdx > 0 ? 'border-t border-gray-100' : ''}
                    >
                      {/* Group label */}
                      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-5 pt-3.5 pb-1">
                        {group.label}
                      </p>

                      {/* Hits */}
                      {group.items.map((hit, hitIdx) => {
                        const globalIdx = hitGlobalIndex(results!, groupIdx, hitIdx);
                        const isActive = globalIdx === activeIndex;
                        const Icon = ENTITY_ICON[hit.entity] ?? FileText;

                        return (
                          <button
                            key={`${hit.entity}-${hit.id}`}
                            type="button"
                            role="option"
                            aria-selected={isActive}
                            onClick={() => goToHit(hit)}
                            onMouseEnter={() => setActiveIndex(globalIdx)}
                            className={`w-full flex items-center gap-3 px-5 py-3 text-left transition-colors ${
                              isActive ? 'bg-indigo-50' : 'hover:bg-gray-50'
                            }`}
                          >
                            <span
                              className={`flex-shrink-0 p-2 rounded-lg ${
                                isActive
                                  ? 'bg-indigo-100 text-indigo-600'
                                  : 'bg-gray-100 text-gray-500'
                              }`}
                            >
                              <Icon className="h-4 w-4" />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block text-sm font-medium text-gray-900 truncate">
                                {hit.title}
                              </span>
                              <span className="block text-xs text-gray-500 truncate mt-0.5">
                                {hit.subtitle}
                              </span>
                            </span>
                            {isActive && (
                              <span className="flex-shrink-0 text-[10px] text-indigo-400 font-medium bg-indigo-50 border border-indigo-100 rounded px-1.5 py-0.5">
                                Enter ↵
                              </span>
                            )}
                          </button>
                        );
                      })}

                      {/* See all */}
                      <button
                        type="button"
                        onClick={() => {
                          navigate(seeAllGroupRoute(group, query.trim()));
                          closeOverlay();
                        }}
                        className="w-full flex items-center gap-1.5 px-5 py-2.5 text-xs text-indigo-600 font-medium hover:bg-indigo-50 transition-colors"
                      >
                        <ChevronRight className="h-3.5 w-3.5" />
                        See all {group.total} {group.label.toLowerCase()}
                      </button>
                    </div>
                  );
                })}
            </div>

            {/* ── Footer hint ───────────────────────────────────────────── */}
            {(hasResults || noResults) && (
              <div className="flex items-center gap-4 px-5 py-2.5 border-t border-gray-100 bg-gray-50 text-[11px] text-gray-400">
                <span><kbd className="font-mono bg-white border border-gray-200 rounded px-1">↑↓</kbd> navigate</span>
                <span><kbd className="font-mono bg-white border border-gray-200 rounded px-1">↵</kbd> open</span>
                <span><kbd className="font-mono bg-white border border-gray-200 rounded px-1">Esc</kbd> close</span>
              </div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
